import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import Swal from "sweetalert2";
import {
  getAllContactForms,
  getContactFormById,
  deleteContactForm,
} from "../../services/Contact/contactUsServices";
import { calculateTotalPages, BootstrapTooltip } from "../../assets/js/script";
import TableHeader from "../Common/TableComponent/TableHeader";
import EntriesDropdown from "../Common/TableComponent/EntriesDropdown";
import { Pagination } from "../Common/TableComponent/Pagination";
import { Loading } from "../Common/OtherElements/Loading";
import { TableDataStatusError } from "../Common/OtherElements/TableDataStatusError";
import { confirmDelete } from "../Common/OtherElements/confirmDeleteClone";
import { handleErrors } from "../../utils/errorHandler";
import { usePageLevelAccess } from "../../hooks/usePageLevelAccess";
import { blsDatePickerOptions } from "../../utils/blsDatePicker";
import { externalLink, showDetailModal } from "../../utils/detailModal";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

export const ManageContact = () => {
  const navigate = useNavigate();
  const PageLevelAccessurl = "contact-form";
  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);
  const [pageAccessDetails, setPageAccessDetails] = useState([]);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [contacts, setContacts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    }
  }, [pageAccessData, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await getAllContactForms({
          pageNo: currentPage,
          pageSize: entriesPerPage,
          fromDate: fromDate || null,
          toDate: toDate || null,
          search,
        });
        const rows = Array.isArray(data) ? data : [];
        setContacts(rows);
        setTotalCount(rows[0]?.totalCount || 0);
      } catch (error) {
        setContacts([]);
        setTotalCount(0);
        handleErrors(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage, entriesPerPage, fromDate, toDate, search, reloadToken]);

  const totalPages = calculateTotalPages(totalCount, entriesPerPage);

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const applySearch = () => {
    setCurrentPage(1);
    setSearch(searchInput.trim());
  };

  const handleView = async (id) => {
    try {
      const data = await getContactFormById(id);
      showDetailModal({
        title: data.subject || "Contact Form",
        rows: [
          { label: "Name", value: data.fullName },
          { label: "Email", value: data.email },
          { label: "Phone", value: data.phoneNumber },
          { label: "Company", value: data.companyName },
          { label: "Subject", value: data.subject },
          { label: "Message", value: data.message },
          { label: "Page URL", html: externalLink(data.pageUrl) },
        ],
      });
    } catch (error) {
      handleErrors(error);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDelete("Contact Form");
    if (!confirmed) return;

    try {
      await deleteContactForm(id);
      Swal.fire(
        "Deleted!",
        "The contact form has been deleted successfully.",
        "success"
      );
      setReloadToken((token) => token + 1);
    } catch (error) {
      handleErrors(error);
    }
  };

  return (
    <>
      <style>
        {`
          .contact-toolbar {
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
            gap: 16px;
            flex-wrap: wrap;
          }
          .contact-toolbar .form-select-entry-count {
            margin-bottom: 0;
          }
          .contact-toolbar-filters {
            display: flex;
            align-items: flex-end;
            gap: 12px;
            flex-wrap: wrap;
          }
          .contact-toolbar-field .form-label {
            margin-bottom: 4px;
            font-size: 13px;
          }
          .contact-toolbar-field .form-control {
            width: 160px;
          }
          .contact-toolbar-search .form-control {
            width: 240px;
          }
          .contact-toolbar-submit {
            height: 37.5px;
          }
          @media (max-width: 768px) {
            .contact-toolbar,
            .contact-toolbar-filters {
              align-items: stretch;
            }
            .contact-toolbar-field .form-control,
            .contact-toolbar-search .form-control,
            .contact-toolbar-submit {
              width: 100%;
            }
          }
        `}
      </style>
      {pageAccessDetails.viewAccess ? (
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5">
              <div className="card-header d-sm-flex align-items-center justify-content-between">
                <h5 className="mb-sm-2 mt-sm-2">Manage Contact Forms</h5>
              </div>
              <div className="card-body manage-amenity-master-card-body">
                <div className="contact-toolbar mb-3">
                  <div className="contact-toolbar-filters">
                    <div className="contact-toolbar-field">
                      <label htmlFor="contactFromDate" className="form-label">
                        From Date
                      </label>
                      <Flatpickr
                        id="contactFromDate"
                        className="form-control bls-date-input"
                        placeholder="dd-mm-yyyy"
                        value={fromDate}
                        options={blsDatePickerOptions}
                        onChange={([date]) => {
                          setFromDate(date || "");
                          setCurrentPage(1);
                        }}
                      />
                    </div>
                    <div className="contact-toolbar-field">
                      <label htmlFor="contactToDate" className="form-label">
                        To Date
                      </label>
                      <Flatpickr
                        id="contactToDate"
                        className="form-control bls-date-input"
                        placeholder="dd-mm-yyyy"
                        value={toDate}
                        options={blsDatePickerOptions}
                        onChange={([date]) => {
                          setToDate(date || "");
                          setCurrentPage(1);
                        }}
                      />
                    </div>
                    <div className="contact-toolbar-field contact-toolbar-search">
                      <label htmlFor="contactSearch" className="form-label">
                        Search
                      </label>
                      <input
                        id="contactSearch"
                        type="text"
                        placeholder="Name, email, phone, company"
                        className="form-control"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") applySearch();
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary contact-toolbar-submit"
                      onClick={applySearch}
                    >
                      Search
                    </button>
                  </div>
                  <EntriesDropdown
                    entriesPerPage={entriesPerPage}
                    onEntriesChange={handleEntriesChange}
                  />
                </div>

                {loading ? (
                  <Loading />
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-bordered">
                      <TableHeader
                        columns={[
                          "#",
                          "Name",
                          "Email",
                          "Phone",
                          "Company",
                          "Subject",
                          "Added On",
                          "Action",
                        ]}
                      />
                      <tbody className="manage-page-group-table-values">
                        {contacts.length === 0 ? (
                          <TableDataStatusError colspan="8" />
                        ) : (
                          contacts.map((item) => (
                            <tr className="manage-page-group" key={item.id}>
                              <td>{item.rowNumber}</td>
                              <td>{item.fullName || "-"}</td>
                              <td>{item.email || "-"}</td>
                              <td>{item.phoneNumber || "-"}</td>
                              <td>{item.companyName || "-"}</td>
                              <td>{item.subject || "-"}</td>
                              <td>{formatDateTime(item.addedOn)}</td>
                              <td>
                                <BootstrapTooltip title="View">
                                  <span
                                    className="cursor-pointer me-2"
                                    onClick={() => handleView(item.id)}
                                  >
                                    <i className="ri-eye-fill"></i>
                                  </span>
                                </BootstrapTooltip>
                                {pageAccessDetails.deleteAccess && (
                                  <BootstrapTooltip title="Delete">
                                    <span
                                      className="cursor-pointer"
                                      onClick={() => handleDelete(item.id)}
                                    >
                                      <i className="ri-delete-bin-6-line"></i>
                                    </span>
                                  </BootstrapTooltip>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalEntries={totalCount}
                  entriesPerPage={entriesPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
    </>
  );
};
